import type { HTMLAttributes, ReactNode } from 'react';
import { initials } from '@titan/shared';
import styles from './Text.module.css';

const cx = (...parts: Array<string | false | undefined | null>) => parts.filter(Boolean).join(' ');

/** Lowercase in source, uppercased by CSS. */
export function Eyebrow({ children, className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx(styles.eyebrow, className)} {...rest}>
      {children}
    </div>
  );
}

export function SectionTitle({ title, description }: { title: ReactNode; description?: ReactNode }) {
  return (
    <div>
      <h2 className={styles.sectionTitle}>{title}</h2>
      {description && <p className={styles.sectionDescription}>{description}</p>}
    </div>
  );
}

export interface AvatarProps {
  name: string;
  variant?: 'light' | 'dark';
}

/** Circular initials avatar. */
export function Avatar({ name, variant = 'light' }: AvatarProps) {
  return (
    <span className={cx(styles.avatar, variant === 'dark' && styles.avatarDark)} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

/** Square gradient tile with an initial, for Moddy and Costy. */
export function AgentTile({ agent }: { agent: 'moddy' | 'costy' }) {
  return (
    <span className={cx(styles.avatar, styles.avatarTile, styles[agent])} aria-hidden="true">
      {agent === 'moddy' ? 'M' : 'C'}
    </span>
  );
}

export function StatusDot({ status }: { status: 'healthy' | 'paused' | 'stopped' | 'draft' }) {
  return <span className={cx(styles.dot, styles[status])} aria-hidden="true" />;
}

export interface DefinitionListProps {
  items: Array<{ label: ReactNode; value: ReactNode; mono?: boolean }>;
}

/** Label left, value right, hairlines between. */
export function DefinitionList({ items }: DefinitionListProps) {
  return (
    <dl className={styles.dl}>
      {items.map((it, i) => (
        <div key={i} className={styles.dlRow}>
          <dt className={styles.dt}>{it.label}</dt>
          <dd className={cx(styles.dd, it.mono && styles.ddMono)}>{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Bullets({ items }: { items: ReactNode[] }) {
  return (
    <ul className={styles.bullets}>
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}

export function Muted({ children, small, className }: { children: ReactNode; small?: boolean; className?: string }) {
  return <span className={cx(styles.muted, small && styles.small, className)}>{children}</span>;
}

export function Mono({ children }: { children: ReactNode }) {
  return <span className={styles.mono}>{children}</span>;
}

/** Monospace tiles, e.g. linked AWS accounts. */
export function Tiles({ children }: { children: ReactNode }) {
  return <div className={styles.tiles}>{children}</div>;
}
export function Tile({ children, empty }: { children: ReactNode; empty?: boolean }) {
  return <div className={cx(styles.tile, empty && styles.tileEmpty)}>{children}</div>;
}
